import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import GroupsService from '../../services/groups.service';
import TeamsService from '../../services/teams.service';
import { Team } from '../../models/training.models';
import { UserAvatar } from '../user-avatar/user-avatar.component';
import { palette } from '../../theme';

interface TpCreateGroupDialogProps {
  open: boolean;
  team: Team;
  onClose: () => void;
  onCreated: () => void;
}

const TpCreateGroupDialog: FC<TpCreateGroupDialogProps> = (props) => {
  const groupsService = useService(GroupsService);
  const teamsService = useService(TeamsService);
  const { showSnackbar } = useSnackbarHelper();

  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [athletes, setAthletes] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.open) {
      setName('');
      setSearch('');
      setSelectedIds(new Set());
      teamsService
        .getAllTeamMembers(props.team.id as string)
        .then((response: any) => setAthletes(response.data.data || []))
        .catch(() => setAthletes([]));
    }
  }, [props.open]);

  const filteredAthletes = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return athletes;
    return athletes.filter((athlete) => (athlete.name || '').toLowerCase().includes(query));
  }, [athletes, search]);

  const toggleAthlete = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = () => {
    if (!name.trim()) {
      showSnackbar('Group name is required', 'error');
      return;
    }
    setLoading(true);
    groupsService
      .addGroup(props.team.id as string, name.trim())
      .then(async (response: any) => {
        const groups = response.data.data || [];
        const created = groups.find((g: any) => g.name === name.trim()) || groups[groups.length - 1];
        if (created && selectedIds.size > 0) {
          await groupsService.addMembersToGroup(props.team.id as string, created.id, Array.from(selectedIds));
        }
        showSnackbar('Group created', 'success');
        props.onCreated();
        props.onClose();
      })
      .catch(() => showSnackbar('Error creating group', 'error'))
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      <DialogTitle style={{ color: palette.purple.main, fontWeight: 700 }}>Create Group</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          label="Group name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <TextField
          fullWidth
          size="small"
          placeholder="Search athletes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          {selectedIds.size} athlete{selectedIds.size === 1 ? '' : 's'} selected
        </Typography>
        <List dense style={{ maxHeight: 280, overflow: 'auto' }}>
          {filteredAthletes.map((athlete) => {
            const id = String(athlete.id);
            return (
              <ListItem key={id} dense disablePadding>
                <FormControlLabel
                  style={{ width: '100%', marginLeft: 0 }}
                  control={
                    <Checkbox
                      checked={selectedIds.has(id)}
                      onChange={() => toggleAthlete(id)}
                      style={{ color: palette.purple.main }}
                    />
                  }
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <ListItemAvatar style={{ minWidth: 0 }}>
                        <UserAvatar name={athlete.name} src={athlete.profileImage} userId={athlete.id} size={32} />
                      </ListItemAvatar>
                      <ListItemText primary={athlete.name} secondary={athlete.email} />
                    </div>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={loading}
          style={{ backgroundColor: palette.purple.main }}
        >
          Create Group
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TpCreateGroupDialog;
